import { formatNumber } from '../../../utils/format';

/**
 * Copy helpers shared by the calendar, the OOO reconciler and the OOO
 * requests list, so the three screens say the same thing the same way.
 */

/** "8 hours" · "4.5 hours" · "Under 4 hours" (0 is the half-day sentinel). */
export const formatHours = (hours: number) => {
  if (hours === 0) return 'Under 4 hours';
  const text = formatNumber(hours, Number.isInteger(hours) ? 0 : 1);
  return `${text} ${hours === 1 ? 'hour' : 'hours'}`;
};

/** What we know about a failed request: an HTTP status, "network" when nothing came back, or nothing. */
export interface RequestFailure {
  status: number | 'network' | null;
  /** Technical detail for operators, shown behind a "Details" disclosure. */
  detail: string | null;
}

/**
 * Reads an axios error (`err.response.status`) or an RTK Query
 * `fetchBaseQuery` error (`err.status`) without trusting its shape.
 */
export const describeRequestFailure = (err: unknown): RequestFailure => {
  if (!err || typeof err !== 'object') return { status: null, detail: null };
  const e = err as {
    response?: { status?: unknown };
    request?: unknown;
    status?: unknown;
    error?: unknown;
    message?: unknown;
  };
  const message = typeof e.message === 'string' ? e.message : null;

  if (e.response && typeof e.response.status === 'number') {
    return { status: e.response.status, detail: message ?? `HTTP ${e.response.status}` };
  }
  if (typeof e.status === 'number') {
    return { status: e.status, detail: `HTTP ${e.status}` };
  }
  if (e.status === 'FETCH_ERROR' || e.status === 'TIMEOUT_ERROR') {
    return { status: 'network', detail: typeof e.error === 'string' ? e.error : String(e.status) };
  }
  if (message === 'Network Error' || (e.request && !e.response)) {
    return { status: 'network', detail: message };
  }
  return { status: null, detail: message };
};

/**
 * The "why + what to do" sentence. It names a cause only when the response
 * tells us one; otherwise it just says what to do.
 */
export const failureAdvice = (failure: RequestFailure | null | undefined, subject: string) => {
  switch (failure?.status) {
    case 403:
      return `You don’t have access to ${subject}. Ask an admin if you need it.`;
    case 401:
      return 'Your session has ended. Sign in again, then retry.';
    case 'network':
      return 'The server didn’t respond. Check your connection and try again in a minute.';
    default:
      return 'Try again in a minute.';
  }
};
